import { Button } from '@atlas/ui-primitives'
import { MessagePickerDialog } from '@/components/messages/MessagePickerDialog'
import type { CampaignCapabilities } from '@/domain/campaign.types'
import { MessageResolutionMatrix } from '@/pages/Messages/MessageResolutionMatrix'
import type { useCampaignMessageMatrixContainer } from './useCampaignMessageMatrixContainer'

type MessagesSection = ReturnType<typeof useCampaignMessageMatrixContainer>

/**
 * The Campaign's Messages section: the Messages feature's own resolution matrix,
 * over the copy associated with THIS Campaign. Every action the section offers is
 * the grid's — Add message (created, classified as the Campaign is, and linked to
 * it), Add locale, Apply AI translation, and the row-level Edit — plus Link
 * existing for copy that already exists elsewhere in the library.
 *
 * Each write is gated on its own resolved capability, none derived from another.
 */
export function CampaignDetailMessages({
	messagesSection,
	capabilities,
}: {
	messagesSection: MessagesSection
	capabilities: CampaignCapabilities
}) {
	const { matrix, picker } = messagesSection
	return (
		<MessageResolutionMatrix
			rows={matrix.rows}
			localeColumns={matrix.localeColumns}
			isLoading={matrix.isLoading}
			isError={matrix.isError}
			selection={matrix.selection}
			selectedCount={matrix.selectedCount}
			onToggleCell={matrix.toggleCell}
			onToggleRow={matrix.toggleRow}
			onToggleLocale={matrix.toggleLocale}
			availableLocaleOptions={matrix.availableLocaleOptions}
			onAddLocale={matrix.addLocale}
			onApplyTranslation={matrix.applyTranslation}
			pendingOverwrites={matrix.pendingOverwrites}
			onConfirmApplyOverwrites={matrix.confirmApplyOverwrites}
			onCancelApplyOverwrites={matrix.cancelApplyOverwrites}
			isApplying={matrix.isApplying}
			batchError={matrix.batchError}
			edit={matrix.edit}
			add={matrix.add}
			heading="Messages"
			emptyLabel="No Messages associated yet."
			capabilities={{
				canAddMessage: capabilities.canAssociateMessages,
				canTranslate: capabilities.canTranslateMessages,
				canEditRows: capabilities.canEditMessages,
			}}
			extraActions={
				capabilities.canAssociateMessages ? (
					<Button variant="secondary" className="rounded-ds-full" onPress={picker.onOpenPicker}>
						Link existing
					</Button>
				) : null
			}
		/>
	)
}

/** The link-existing picker, kept beside the section it belongs to rather than in the page. */
export function CampaignDetailMessagePicker({ picker }: { picker: MessagesSection['picker'] }) {
	return (
		<MessagePickerDialog
			isOpen={picker.isPickerOpen}
			candidates={picker.pickerCandidates}
			isLoading={picker.isPickerLoading}
			isError={picker.isPickerError}
			search={picker.pickerSearch}
			onSearchChange={picker.onPickerSearchChange}
			selectedIds={picker.pickerSelectedIds}
			onToggleSelection={picker.togglePickerSelection}
			isAssociationPending={picker.isAssociationPending}
			isAssociationError={picker.isAssociationError}
			isAssociationSuccess={picker.isAssociationSuccess}
			onConfirm={picker.onConfirmPicker}
			onRetry={picker.onRetryPickerAssociation}
			onCancel={picker.onClosePicker}
		/>
	)
}
